import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Eye, FileText, Trash2 } from "lucide-react";
import { Week } from "../types/workout-types";

interface WorkoutTableHeaderProps {
  weeks: Week[];
  currentWeekIndex: number;
  currentDayIndex: number;
  setCurrentWeekIndex: (index: number) => void;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  handleUndo: () => void;
  handleRedo: () => void;
  historyLength: number;
  redoStackLength: number;
}

export function WorkoutTableHeader({
  weeks,
  currentWeekIndex,
  currentDayIndex,
  setCurrentWeekIndex,
  onPreviousWeek,
  onNextWeek,
  handleUndo,
  handleRedo,
  historyLength,
  redoStackLength,
}: WorkoutTableHeaderProps) {
  // Get the current day focus or default title
  const currentDay = weeks[currentWeekIndex]?.days[currentDayIndex];
  const dayNumber = currentDay?.day_number || (currentDayIndex + 1);
  const focus = currentDay?.focus || `Day ${dayNumber}`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="icon"
            onClick={onPreviousWeek}
            disabled={currentWeekIndex === 0}
            title="Previous Week"
            className="text-white border-white/20 hover:bg-white/10"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <Select
            value={currentWeekIndex.toString()}
            onValueChange={(value) => setCurrentWeekIndex(Number.parseInt(value))}
          >
            <SelectTrigger className="w-[180px] bg-white/10 border-white/20 text-white">
              <SelectValue placeholder="Select Week" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-white/20 text-white">
              {weeks.map((week, index) => (
                <SelectItem key={week.id} value={index.toString()} className="hover:bg-white/10">
                  {week.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="icon"
            onClick={onNextWeek}
            disabled={currentWeekIndex === weeks.length - 1}
            title="Next Week"
            className="text-white border-white/20 hover:bg-white/10"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex space-x-2">
          <button
            className="p-1 hover:bg-white/10 rounded text-white"
            onClick={handleUndo}
            title="Undo (Ctrl+Z)"
            disabled={historyLength <= 1}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={historyLength <= 1 ? "text-white/30" : ""}
            >
              <path d="M3 7v6h6" />
              <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
            </svg>
          </button>
          <button
            className="p-1 hover:bg-white/10 rounded text-white"
            onClick={handleRedo}
            title="Redo (Ctrl+Y)"
            disabled={redoStackLength === 0}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={redoStackLength === 0 ? "text-white/30" : ""}
            >
              <path d="M21 7v6h-6" />
              <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" />
            </svg>
          </button>
          <button className="p-1 hover:bg-white/10 rounded text-white">
            <Eye className="h-4 w-4" />
          </button>
          <button className="p-1 hover:bg-white/10 rounded text-white">
            <FileText className="h-4 w-4" />
          </button>
          <button className="p-1 hover:bg-white/10 rounded text-white">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      {/* Display current selected day and focus */}
      <div className="text-white text-xl font-semibold">
        {focus}
      </div>
    </div>
  );
}
